import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DynamicPropertyPage } from './DynamicPropertyPage';

const sampleProperty = {
  license_number: 'VUT-ES-000001',
  property_data: {
    name: {
      es: 'Casa Brisa Marina',
      en: 'Sea Breeze House'
    },
    description: {
      es: 'Casa Brisa Marina es un alojamiento frente al mar con vistas panorámicas, piscina privada y espacios diseñados para familias y grupos. Disfruta de una ubicación privilegiada cerca de la playa, restaurantes y actividades náuticas.',
      en: 'Sea Breeze House is a beachfront stay with panoramic views, a private pool, and spaces designed for families and groups. Enjoy a prime location close to the beach, restaurants, and water activities.'
    },
    stats: {
      guests: 8,
      bedrooms: 4,
      beds: 5,
      bathrooms: 3
    },
    rules: {
      es: [
        'No se permiten fiestas ni eventos',
        'No se admiten mascotas',
        'No fumar en el interior'
      ],
      en: [
        'No parties or events allowed',
        'No pets allowed',
        'No smoking indoors'
      ]
    },
    check_in: '16:00 - 21:00',
    check_out: '11:00',
    cancellation_policy: {
      es: 'Cancelación gratuita hasta 7 días antes de la llegada. Después de ese plazo se cobra el 50% del total de la reserva.',
      en: 'Free cancellation up to 7 days before arrival. After that deadline, 50% of the total booking amount will be charged.'
    }
  },
  images: [
    { url: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80' },
    { url: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80' },
    { url: 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80' },
    { url: 'https://images.unsplash.com/photo-1472220625704-91e1462799b2?auto=format&fit=crop&w=1200&q=80' },
    { url: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1200&q=80' }
  ],
  location: {
    google_maps_address: 'Passeig de Jaume I, Salou, Tarragona, España',
    latitude: 41.0735281,
    longitude: 1.1428815,
    area_description: {
      es: 'Ubicado a pocos pasos de la playa, con acceso rápido a restaurantes, senderos y actividades acuáticas.',
      en: 'Located just a few steps from the beach, with quick access to restaurants, walking trails, and water activities.'
    },
    nearby: {
      es: ['200 m de la playa', '5 min a pie de cafeterías', '10 min en coche de tiendas'],
      en: ['200 m from the beach', '5 min walk to cafés', '10 min drive to shops']
    }
  },
  pricing: {
    min_price: 120,
    max_price: 320,
    pricing_mode: 'advanced',
    base_price: 140,
    seasonal_rates: [
      {
        id: 'season-summer',
        label: 'Verano',
        date_start: '2026-07-01',
        date_end: '2026-08-31',
        rate_type: 'percentage',
        rate_value: 45,
      },
      {
        id: 'season-holidays',
        label: 'Navidad y Fin de Ano',
        date_start: '2026-12-20',
        date_end: '2027-01-07',
        rate_type: 'fixed',
        rate_value: 290,
      }
    ],
    weekend_surcharge: {
      enabled: true,
      type: 'fixed',
      value: 25,
    },
    duration_discounts: {
      week: 10,
      month: 20,
    }
  },
  contact: {
    email: 'contacto@casabrisamarina.com',
    whatsapp: '+34123456789'
  },
  services: ['Wi-Fi rápido', 'Piscina privada', 'Aire acondicionado', 'Cocina equipada', 'Limpieza incluida'],
  booking: {
    booking_links: [
      'https://www.airbnb.com/rooms/sample123',
      'https://www.booking.com/hotel/sample123.html'
    ],
    ics_urls: []
  },
  plan: 'premium',
  blocked_dates: [
    '2026-04-15', '2026-04-16', '2026-04-17',
    '2026-05-01', '2026-05-02', '2026-05-03', '2026-05-04', '2026-05-05',
    '2026-05-10', '2026-05-11', '2026-05-12',
    '2026-05-20', '2026-05-21', '2026-05-22', '2026-05-23',
    '2026-06-01', '2026-06-02'
  ]
};

export const SamplePropertyPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('basic');

  const planConfig = {
    basic: {
      title: 'Básico',
      price: '49€',
      subtitle: 'Web profesional esencial',
      features: {
        languageToggle: false,
        availabilityCalendar: false,
        customDomain: false,
      },
    },
    plus: {
      title: 'Plus',
      price: '69€',
      subtitle: 'Más visibilidad y automatización',
      features: {
        languageToggle: true,
        availabilityCalendar: true,
        customDomain: false,
      },
    },
    premium: {
      title: 'Premium',
      price: '89€',
      subtitle: 'Marca propia',
      features: {
        languageToggle: true,
        availabilityCalendar: true,
        customDomain: true,
      },
    },
  };

  const featureRows = [
    { key: 'languageToggle', label: 'Selector de idioma ES/EN' },
    { key: 'availabilityCalendar', label: 'Calendario de disponibilidad sincronizado' },
    { key: 'customDomain', label: 'Dominio personalizado' },
  ];

  const samplePropertyByPlan = useMemo(() => {
    const base = JSON.parse(JSON.stringify(sampleProperty));
    base.plan = selectedPlan;

    if (selectedPlan === 'basic') {
      base.blocked_dates = [];
      base.booking = {
        ...(base.booking || {}),
        ics_urls: [],
        custom_domain: null,
      };
    }

    if (selectedPlan === 'plus') {
      base.booking = {
        ...(base.booking || {}),
        custom_domain: null,
      };
    }

    if (selectedPlan === 'premium') {
      base.booking = {
        ...(base.booking || {}),
        custom_domain: 'www.casabrisamarina.com',
      };
    }

    return base;
  }, [selectedPlan]);

  return (
    <div>
      <section className="bg-slate-900 text-white py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold">Demo interactiva por plan</h1>
          <p className="mt-2 text-slate-200 max-w-3xl">
            Cambia de plan para ver cómo se comporta la página de ejemplo en cada modalidad.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/submit"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 font-bold px-7 py-3 text-base shadow-lg hover:from-emerald-300 hover:to-teal-400 transition"
            >
              Crear mi página ahora
            </Link>
            <p className="text-sm text-slate-300">
              Empezarás con el formulario completo y podrás elegir el plan que prefieras.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {Object.entries(planConfig).map(([planKey, plan]) => {
              const isActive = selectedPlan === planKey;
              return (
                <button
                  key={planKey}
                  type="button"
                  onClick={() => setSelectedPlan(planKey)}
                  className={`text-left rounded-xl border px-5 py-4 transition ${
                    isActive
                      ? 'bg-teal-600 border-teal-300 shadow-lg shadow-teal-900/40'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-lg">{plan.title}</span>
                    <span className="text-sm font-semibold">{plan.price}</span>
                  </div>
                  <p className="text-sm mt-1 text-slate-100">{plan.subtitle}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl border border-slate-700 bg-slate-800/80 overflow-hidden">
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-0 text-sm">
              <div className="px-4 py-3 font-semibold border-b border-slate-700">Funcionalidad</div>
              <div className="px-4 py-3 font-semibold border-b border-slate-700 text-center">Básico</div>
              <div className="px-4 py-3 font-semibold border-b border-slate-700 text-center">Plus</div>
              <div className="px-4 py-3 font-semibold border-b border-slate-700 text-center">Premium</div>

              {featureRows.map((feature) => (
                <React.Fragment key={feature.key}>
                  <div className="px-4 py-3 border-b border-slate-700/70 text-slate-100">{feature.label}</div>
                  {['basic', 'plus', 'premium'].map((planKey) => (
                    <div key={`${feature.key}-${planKey}`} className="px-4 py-3 border-b border-slate-700/70 text-center">
                      {planConfig[planKey].features[feature.key] ? '✅' : '—'}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      <DynamicPropertyPage sampleProperty={samplePropertyByPlan} />
    </div>
  );
};
